package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Les trois entités de la carte heuristique dans le moteur de synchronisation.
 * À part de [SyncEntityOps], qui ne fait que déléguer : ce fichier reste lisible
 * et le gros `when` ne grossit pas.
 */
internal object MindMapSyncOps {

    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = when (entity) {
        EntityKind.MindMap -> db.mindMapQueries.selectDirty()
        EntityKind.MindMapNode -> db.mindMapNodeQueries.selectDirty()
        EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.selectDirty()
        else -> notMine(entity)
    }

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.MindMap -> db.mindMapQueries.selectDirty().executeAsList().map { mapRow(it) }
        EntityKind.MindMapNode -> db.mindMapNodeQueries.selectDirty().executeAsList().map { nodeRow(it) }
        EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.selectDirty().executeAsList().map { linkRow(it) }
        else -> notMine(entity)
    }

    fun selectAll(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.MindMap -> db.mindMapQueries.selectAll().executeAsList().map { mapRow(it) }
        EntityKind.MindMapNode -> db.mindMapNodeQueries.selectAll().executeAsList().map { nodeRow(it) }
        EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.selectAll().executeAsList().map { linkRow(it) }
        else -> notMine(entity)
    }

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        when (entity) {
            EntityKind.MindMap -> db.mindMapQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.MindMapNode -> db.mindMapNodeQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.markSynced(syncedAt, id, updatedAt)
            else -> notMine(entity)
        }
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? = when (entity) {
        EntityKind.MindMap -> db.mindMapQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.MindMapNode -> db.mindMapNodeQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        else -> notMine(entity)
    }

    fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.MindMap -> db.mindMapQueries.insert(
                row.id,
                p.str("title") ?: "",
                p.str("color"),
                p.str("skill_id"),
                p.str("objective_id"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.MindMapNode -> db.mindMapNodeQueries.insert(
                row.id,
                p.str("map_id") ?: "",
                p.str("parent_id"),
                p.str("text") ?: "",
                p.str("color"),
                p.str("source_kind"),
                p.str("source_id"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.insert(
                row.id,
                p.str("node_id") ?: "",
                p.str("note_id") ?: "",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            else -> notMine(entity)
        }
    }

    fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.MindMap -> db.mindMapQueries.update(
                p.str("title") ?: "",
                p.str("color"),
                p.str("skill_id"),
                p.str("objective_id"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.MindMapNode -> db.mindMapNodeQueries.update(
                p.str("map_id") ?: "",
                p.str("parent_id"),
                p.str("text") ?: "",
                p.str("color"),
                p.str("source_kind"),
                p.str("source_id"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.update(
                p.str("node_id") ?: "",
                p.str("note_id") ?: "",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            else -> notMine(entity)
        }
    }

    fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow, deletedAt: Long) {
        when (entity) {
            EntityKind.MindMap -> db.mindMapQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.MindMapNode -> db.mindMapNodeQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.softDelete(deletedAt, row.updatedAt, row.id)
            else -> notMine(entity)
        }
    }

    fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        when (entity) {
            EntityKind.MindMap -> db.mindMapQueries.restore(row.updatedAt, row.id)
            EntityKind.MindMapNode -> db.mindMapNodeQueries.restore(row.updatedAt, row.id)
            EntityKind.MindMapNodeNote -> db.mindMapNodeNoteQueries.restore(row.updatedAt, row.id)
            else -> notMine(entity)
        }
    }

    private fun mapRow(it: com.tedd.db.Mind_map) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "title" to it.title,
        "color" to it.color,
        "skill_id" to it.skill_id,
        "objective_id" to it.objective_id,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun nodeRow(it: com.tedd.db.Mind_map_node) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "map_id" to it.map_id,
        "parent_id" to it.parent_id,
        "text" to it.text,
        "color" to it.color,
        "source_kind" to it.source_kind,
        "source_id" to it.source_id,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun linkRow(it: com.tedd.db.Mind_map_node_note) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "node_id" to it.node_id,
        "note_id" to it.note_id,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun notMine(entity: EntityKind): Nothing = error("${entity.tableName} n'est pas une entité de carte heuristique")
}
